import { useEffect, useMemo } from 'react';
import { useCommandPaletteContext } from '../CommandPaletteContext';
import type { Command } from '../types';

/**
 * Options for the useRegisterCommands hook
 */
export interface UseRegisterCommandsOptions {
  /**
   * Unique identifier for this plugin
   */
  pluginId: string;

  /**
   * Array of commands to register
   */
  commands: Command[];

  /**
   * Whether the plugin is enabled. If false, commands will not be registered.
   * @default true
   */
  enabled?: boolean;
}

/**
 * Hook for registering commands into the command palette
 * 
 * This hook allows plugins to register their commands into the central
 * command registry. Commands are automatically unregistered when the
 * component unmounts or when the plugin is disabled.
 * 
 * @example
 * ```tsx
 * export const MyPlugin: React.FC = () => {
 *   const dispatch = useDispatch();
 *   
 *   const commands = useMemo(() => [
 *     {
 *       id: 'my-command',
 *       label: 'My Command',
 *       category: 'My Category',
 *       primary: {
 *         id: 'my-action',
 *         label: 'Execute',
 *         action: () => dispatch(myAction()),
 *       },
 *     },
 *   ], [dispatch]);
 *   
 *   useRegisterCommands({
 *     pluginId: 'my-plugin',
 *     commands,
 *   });
 *   
 *   return null;
 * };
 * ```
 */
export function useRegisterCommands({
  pluginId,
  commands,
  enabled = true,
}: UseRegisterCommandsOptions): void {
  const { registerCommands, unregisterCommands } = useCommandPaletteContext();

  // Filter out disabled and hidden commands
  const enabledCommands = useMemo(() => {
    return commands.filter(cmd => cmd.enabled !== false && !cmd.hidden);
  }, [commands]);

  // Register commands when enabled, unregister on cleanup
  useEffect(() => {
    if (enabled) {
      registerCommands(pluginId, enabledCommands);
    }

    return () => {
      unregisterCommands(pluginId);
    };
  }, [pluginId, enabledCommands, enabled, registerCommands, unregisterCommands]);
}
